import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import {
  Loader2,
  GraduationCap,
  Award,
  Globe,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

export default function Login() {
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (!loading && user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    const r = await login(identifier.trim(), password);
    setBusy(false);
    if (r.ok) {
      toast.success("Welcome back!");
      navigate("/");
    } else setErr(r.error || "Login failed");
  };

  return (
    <div
      className="min-h-screen flex"
      style={{ background: "var(--erp-background)" }}
    >
      {/* Left visual */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden erp-gradient text-white">
        {/* Subtle dot pattern */}
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
            backgroundSize: "24px 24px",
          }}
        />
        <div
          className="absolute inset-0 mix-blend-soft-light opacity-30"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1621192754911-ffe0d95929dd?crop=entropy&cs=srgb&fm=jpg&q=85)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />

        <div className="m-auto max-w-md px-12 relative z-10">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur border border-white/30 flex items-center justify-center font-bold text-xl">
              <GraduationCap className="w-6 h-6" />
            </div>
            <div>
              <div className="text-xl font-bold font-serif-display tracking-tight">
                School ERP
              </div>
              <div className="text-[11px] uppercase tracking-widest opacity-70">
                National Edition · India
              </div>
            </div>
          </div>

          <h1 className="text-4xl font-bold mb-3 leading-tight tracking-tight font-serif-display">
            Empowering schools
            <br /> across{" "}
            <span style={{ color: "var(--erp-seal)" }}>Bharat</span>.
          </h1>
          <p className="text-white/80 text-sm leading-relaxed">
            One elegant dashboard for admissions, attendance, fees, staff,
            library and everything in between — multi-branch, multi-session,
            CBSE/ICSE/State board ready, Tally-friendly.
          </p>

          <div className="mt-10 grid grid-cols-2 gap-3">
            {[
              { icon: ShieldCheck, label: "UDISE+ / APAAR" },
              { icon: Award, label: "CBSE • ICSE • State" },
              { icon: Globe, label: "Multi-branch" },
              { icon: Sparkles, label: "Tally-style Ledger" },
            ].map((f) => (
              <div
                key={f.label}
                className="flex items-center gap-2 border border-white/20 rounded-lg px-3 py-2.5 bg-white/5 backdrop-blur-sm text-xs font-medium"
              >
                <f.icon
                  className="w-3.5 h-3.5"
                  style={{ color: "var(--erp-seal)" }}
                />
                {f.label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 py-8">
        <form
          onSubmit={onSubmit}
          className="w-full max-w-sm space-y-5"
          data-testid="login-form"
        >
          {/* Mobile-only brand */}
          <div className="flex lg:hidden items-center justify-center gap-2 mb-2">
            <div className="w-10 h-10 erp-gradient rounded-xl flex items-center justify-center text-white">
              <GraduationCap className="w-5 h-5" />
            </div>
            <div>
              <div className="font-bold font-serif-display erp-text-primary">
                School ERP
              </div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">
                National Edition
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight font-serif-display erp-text-primary">
              Welcome back
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Sign in with your email or mobile number
            </p>
          </div>

          {err && (
            <Alert variant="destructive" data-testid="login-error">
              <AlertDescription>{err}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label
              htmlFor="identifier"
              className="text-xs font-semibold text-slate-700"
            >
              Email or Mobile
            </Label>
            <Input
              id="identifier"
              data-testid="login-identifier"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="Enter email or mobile number"
              className="h-10"
              required
            />
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="password"
              className="text-xs font-semibold text-slate-700"
            >
              Password
            </Label>
            <Input
              id="password"
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="h-10"
              required
            />
          </div>

          <Button
            type="submit"
            disabled={busy}
            data-testid="login-submit"
            className="w-full h-10 erp-primary text-white font-semibold shadow-md"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
          </Button>
        </form>
      </div>
    </div>
  );
}
